import { revealValidationSummary } from '@/components/customer-details-form/validationSummaryReveal';
import { CustomerDetailsForm } from '@/components/customer-details-form/CustomerDetailsForm';
import { ImportInfo } from '@/components/order/ImportInfo';
import OrderPageShell from '@/components/order/OrderPageShell';
import { SelectPaymentMethod } from '@/components/purchase/checkout/SelectPaymentMethod';
import { Money } from '@/lib/money';
import { OrderApi, PendingPersonalisationTicket as PendingPersonalisationTicketType} from '@/lib/OrderApi';
import { useLanguage } from '@/providers/LanguageProvider';
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react';
import { StyleOverwrites } from '@/providers/StyleOverwrites';
import { shopVar } from '@/lib/shopStyles';
import { MainButton } from '@/components/shared/MainButton';
import { calculateFee } from '@/lib/calculateFee';
import { handleRedirect } from '@/lib/iframeRedirect';
import * as m from "@/paraglide/messages.js"

type shareCodeSearch = {
    orderId: string|undefined;
}

export const Route = createFileRoute('/ticket-share/$shareCode')({
    validateSearch: (search: Record<string, unknown>): shareCodeSearch => {
        return {
            orderId: search.orderId as string|undefined
        }
    },
    loaderDeps: ({search: {orderId}}) => ({orderId}),
    loader: async ({ params, deps: {orderId} }) => {
        const ticketApi = new OrderApi(import.meta.env.VITE_API_URL);
        // Load with default language first, will be updated in component if needed
        return { pendingPersonalisationTicket: await ticketApi.getPendingPersonalisationTicketByCode(params.shareCode, orderId)}
    },
    errorComponent: () => {
        return <div>{m.ticket_share_code_not_found()}</div>
      },
  component: PendingPersonalisationTicket
})

function PendingPersonalisationTicket() {
    const { pendingPersonalisationTicket } = Route.useLoaderData();

    const { orderId } = Route.useSearch()
    const { currentLanguage: lang } = useLanguage();

    const ticketApi = new OrderApi(import.meta.env.VITE_API_URL);


    const splitName = (full: string | null | undefined): { first_name: string; last_name: string } => {
        if (!full) return { first_name: '', last_name: '' };
        const trimmed = full.trim();
        const firstSpace = trimmed.indexOf(' ');
        if (firstSpace === -1) return { first_name: trimmed, last_name: '' };
        const first = trimmed.slice(0, firstSpace);
        const rest = trimmed.slice(firstSpace + 1).trimStart();
        const last = rest.length === 0 ? '' : rest.charAt(0).toUpperCase() + rest.slice(1);
        return { first_name: first, last_name: last };
    };

    const [formState, setFormState] = useState<Record<string, object[] | string[]>>({
        // OrderDetails reads answers as an object (answers['first_name']); other form parts
        // receive arrays. The shared Record type is over-broad — cast for the orderDetails case.
        orderDetails: {
            ...splitName(pendingPersonalisationTicket.shared_to),
            email: pendingPersonalisationTicket.shared_to_email ?? '',
        } as unknown as object[],
    });

    const [formValid, setFormValid] = useState(false);
    const [ticket, setTicket] = useState<PendingPersonalisationTicketType>(pendingPersonalisationTicket);
    const [paymentMethod, setPaymentMethod] = useState<string|null>(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const navigate = useNavigate({ from: '/ticket-share/$shareCode' })

    // Reload ticket with current language when language changes
    useEffect(() => {
        const reloadTicketWithLanguage = async () => {
            try {
                const newTicket = await ticketApi.getPendingPersonalisationTicketByCode(
                    pendingPersonalisationTicket.share_code, 
                    orderId, 
                    lang
                );
                setTicket(newTicket);
            } catch (error) {
                console.error('Failed to reload ticket with language:', error);
            }
        };
        
        reloadTicketWithLanguage();
    }, [lang, pendingPersonalisationTicket.share_code, orderId, ticketApi]);


    if(pendingPersonalisationTicket.orderId !== null) {
        navigate({to: "/order/$orderId", params: {orderId: pendingPersonalisationTicket.orderId!}, search: { invoiceSequenceId: undefined }})
    }


    useEffect(() => {
        if(ticket?.payment_pending) {
            const checkPayment = async () => {
                const newTicket = await ticketApi.getPendingPersonalisationTicketByCode(ticket.share_code, orderId);
                setTicket(newTicket);
                if (newTicket.payment_pending) {
                    setTimeout(checkPayment, 500);
                }
            };
            setTimeout(checkPayment, 500);
        }
    }, [ticket?.payment_pending]);
    
    async function claimTicket(): Promise<void> {
        setLoading(true);
        setErrors({}); // Clear previous errors
        try {
            const result = await ticketApi.claimTicket(
                pendingPersonalisationTicket.share_code,
                formState,
                paymentMethod
            );
            if (!result.success) {
                setErrors(result.errors || {});
                setLoading(false);
                return;
            }
            if (result.data.type === "no_payment") {
                navigate({to: "/order/$orderId", params: {orderId: result.data.new_order_id}, search: { invoiceSequenceId: undefined }})
            } else {
                handleRedirect(result.data.url);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <StyleOverwrites shopStyle={pendingPersonalisationTicket.shopStyle}>
            <OrderPageShell event={pendingPersonalisationTicket.event} shopStyle={pendingPersonalisationTicket.shopStyle}>
                <div className='bg-white px-8 -mt-4 rounded-b-md' style={{
                    backgroundColor: shopVar('--shop-panelBackgroundColor'),
                    color: shopVar('--shop-textColor')
                }}>
                    <div className='w-full border-b pb-4 mb-4 border-dashed' style={{
                        borderColor: shopVar('--shop-borderColor')
                    }}>
                    </div>
                    <div className='pb-8'>
                        <h2 className='text-lg font-bold leading-7'><span className='underline'>{pendingPersonalisationTicket.shared_by}</span> {m.ticket_share_invited({ eventName: pendingPersonalisationTicket.event.name })}</h2>
                        <ImportInfo className='py-4'>
                            {pendingPersonalisationTicket.should_pay && <ImportInfo.Item>{m.ticket_share_pay_to_transfer({ sharedBy: pendingPersonalisationTicket.shared_by })}</ImportInfo.Item>}
                            <ImportInfo.Item>{m.ticket_share_claim_before_yours()}</ImportInfo.Item>
                        </ImportInfo>
                        {pendingPersonalisationTicket.should_pay && <div className='flex justify-between items-center'>
                            <div className='flex flex-col gap-1'>
                                <span className='font-semibold'>{pendingPersonalisationTicket.productName}</span>
                                <span className='text-xs tracking-tight'>{Money.fromInteger(pendingPersonalisationTicket.price.amount, pendingPersonalisationTicket.price.currency).display(lang)} + {Money.fromInteger(pendingPersonalisationTicket.service_fee.amount, pendingPersonalisationTicket.service_fee.currency).display(lang)} {m.ticket_share_fee()}</span>
                            </div>
                            <div className='text-xl font-bold'>
                                {Money.fromInteger(pendingPersonalisationTicket.price.amount, pendingPersonalisationTicket.price.currency).add(Money.fromInteger(pendingPersonalisationTicket.service_fee.amount, pendingPersonalisationTicket.service_fee.currency)).display(lang)}
                            </div>
                        </div>}
                        {!pendingPersonalisationTicket.should_pay && <div className='flex justify-between items-center'>
                            <div className='flex flex-col gap-1'>
                                <span className='font-semibold'>{pendingPersonalisationTicket.productName}</span>
                            </div>
                        </div>}

                        <div className='pt-4'>
                            {!ticket.payment_pending && !ticket.paid && (
                            <CustomerDetailsForm
                                form={pendingPersonalisationTicket.form}
                                answers={formState}
                                errors={errors}
                                setAnswers={(answers) => setFormState(answers as Record<string, object[] | string[]>)}
                                setFormValid={setFormValid}
                            />
                            )}
                        </div>

                        {!ticket.payment_pending && !ticket.paid && ticket.should_pay && (
                            <div>
                            <SelectPaymentMethod 
                                className='py-6'
                                paymentMethods={pendingPersonalisationTicket.paymentMethods}
                                selectedMethod={paymentMethod}
                                onMethodChange={setPaymentMethod}
                                additionalFee={pendingPersonalisationTicket.paidShareFee ? calculateFee(
                                    pendingPersonalisationTicket.paidShareFee,
                                    Money.fromInteger(pendingPersonalisationTicket.price.amount, pendingPersonalisationTicket.price.currency),
                                    1
                                ) : undefined}
                                totalAmount={Money.fromInteger(pendingPersonalisationTicket.price.amount, pendingPersonalisationTicket.price.currency)}
                            />
                            <MainButton 
                                disabled={!formValid || !paymentMethod || loading}
                                onDisabledInteraction={revealValidationSummary}
                                loading={loading}
                                onClick={() => claimTicket()}
                                className="mt-4"
                            >
                                <div className="text-lg font-bold leading-7">{m.ticket_share_pay_for_ticket()}</div>
                            </MainButton>
                            </div>
                        )}

                        {ticket.paid && ticket.orderId !== null && (
                            <div>
                                <MainButton 
                                    onClick={() => navigate({to: "/order/$orderId", params: {orderId: ticket.orderId!}, search: { invoiceSequenceId: undefined }})}
                                    className="mt-4"
                                >
                                    <div className="text-lg font-bold leading-7">{m.ticket_share_go_to_ticket()}</div>
                                </MainButton>
                            </div>
                        )}

                        {ticket.paid && ticket.orderId === null && (
                            <div>
                                <h1>{m.ticket_share_claimed_by({ sharedTo: ticket.shared_to ?? '' })}</h1>
                                <p>{m.ticket_share_email_sent({ sharedTo: ticket.shared_to ?? '' })}</p>
                            </div>
                        )}

                        {ticket.payment_pending && !ticket.paid && ticket.should_pay && (
                            <div>
                                <MainButton 
                                    disabled={true}
                                    className="mt-4"
                                >
                                    <div className="text-lg font-bold leading-7">{m.ticket_share_processing_payment()}</div>
                                </MainButton>
                            </div>
                        )}

                        {!ticket.should_pay && !ticket.paid && (
                            <MainButton 
                                disabled={!formValid || loading}
                                loading={loading}
                                onClick={() => claimTicket()}
                                className="mt-4"
                            >
                                <div className="text-lg font-bold leading-7">{m.ticket_share_claim_ticket()}</div>
                            </MainButton>
                        )}
                    </div>
                </div>
            </OrderPageShell>
        </StyleOverwrites>)
}